import { COUNTRY_CODES } from '../data.js';
import { cleanIntelligenceIntake, isIntelligenceService } from './intelligenceServiceWorkflow.js';
import { cleanS02Intake } from './s02Workflow.js';
import { cleanS03Intake } from './s03Workflow.js';
import { cleanS04Intake } from './s04Workflow.js';
import { cleanS15Intake } from './s15Workflow.js';
import { normalizeEpProjectIntake } from './epProjectV2.js';

export type GeographicGroup = { countryFields: string[]; assetFields: string[]; countries: string[]; assets: Array<{ field: string; text: string }> };
export type GeographicScope = { groups: GeographicGroup[] };
export type GeographicConflict = { countries: string[]; detected: string[]; fields: string[] };
export type GeographicReview = { scopeKey: string; countries: string[]; confirmedByUid: string; confirmedByName: string; confirmedAt: string };

const normalize = (value: string) => value.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const names = new Map<string, string>();
const codes = new Map<string, string>();
for (const [name, value] of Object.entries(COUNTRY_CODES)) {
    names.set(normalize(name), name);
    codes.set(value.iso2, name);
    codes.set(value.iso3, name);
}
const aliases: Record<string, string> = {
    'United States of America': 'United States', Britain: 'United Kingdom', 'Great Britain': 'United Kingdom',
    'Kingdom of Saudi Arabia': 'Saudi Arabia', 'Democratic Republic of the Congo': 'Congo, Democratic Republic of the',
    'Democratic Republic of Congo': 'Congo, Democratic Republic of the', 'Republic of the Congo': 'Congo, Republic of the',
    'Republic of Congo': 'Congo, Republic of the', 'Ivory Coast': "Cote d'Ivoire", Türkiye: 'Turkey',
    Czechia: 'Czech Republic', 'Russian Federation': 'Russia', 'Republic of Korea': 'South Korea',
    "Democratic People's Republic of Korea": 'North Korea', 'Viet Nam': 'Vietnam', 'East Timor': 'Timor-Leste',
    'Cape Verde': 'Cabo Verde', Swaziland: 'Eswatini', Burma: 'Myanmar',
};
for (const [alias, name] of Object.entries(aliases))
    if (names.has(normalize(name)))
        names.set(normalize(alias), name);
const abbreviations: Record<string, string> = { UAE: 'United Arab Emirates', KSA: 'Saudi Arabia', USA: 'United States',
    US: 'United States', UK: 'United Kingdom', DRC: 'Congo, Democratic Republic of the', DPRK: 'North Korea', ROK: 'South Korea', PRC: 'China' };
const namePattern = new RegExp(`(?<![\\p{L}\\p{N}])(?:${[...names.keys()].sort((a, b) => b.length - a.length).join('|')})(?![\\p{L}\\p{N}])`, 'gu');
const abbreviationPattern = /(?<![\p{L}\p{N}])(?:U\.?A\.?E\.?|K\.?S\.?A\.?|U\.?S\.?A\.?|U\.?S\.?|U\.?K\.?|DRC|DPRK|ROK|PRC)(?![\p{L}\p{N}])/giu;

export function detectedCountries(value: string): string[] {
    const found = new Set<string>();
    for (const match of normalize(value).matchAll(namePattern))
        found.add(names.get(match[0])!);
    for (const match of value.matchAll(abbreviationPattern)) {
        const alias = match[0].replaceAll('.', '').toUpperCase();
        if (alias !== 'US' || match[0].startsWith('US') || /^u\.s/i.test(match[0]))
            found.add(abbreviations[alias]);
    }
    return [...found].sort();
}

export function geographicCountry(value: unknown): string {
    const raw = String(value ?? '').trim();
    return names.get(normalize(raw)) || abbreviations[raw.toUpperCase().replaceAll('.', '')] || codes.get(raw.toUpperCase()) || '';
}

const text = (value: unknown) => typeof value === 'string' ? value.trim().slice(0, 12000) : '';
const definitions: Record<string, [string[], string[]]> = {
    'S-02': [['fapp_address_country'], ['fapp_address_address', 'fapp_facility_name', 'fapp_objectives_scope']],
    'S-03': [['spp_primary_country'], ['spp_primary_address', 'spp_role_details', 'spp_remote_locations', 'spp_objectives_concerns']],
    'S-04': [['esp_location_country'], ['esp_location_address', 'esp_venue_name', 'esp_event_description']],
    'S-05': [['country'], ['clientAssets']],
    'S-06': [['country'], ['clientExposure']],
    'S-12': [['country'], ['origin_location', 'destination', 'route_primary']],
    'S-15': [['country'], ['siteAddress', 'auditScope']],
    'EP-11': [['venueCountry'], ['venueName', 'venueAddress', 'eventName']],
    'EP-11-legacy': [['country'], ['venueName', 'addressLine1', 'addressLine2', 'eventName']],
    consultant: [['country'], ['assets']],
    generic: [['focusRegionCountry'], ['principalAssetName', 'description']],
};

export function geographicScope(code: string, intake: any): GeographicScope {
    const groups: GeographicGroup[] = [];
    const add = (source: any, countryFields: string[], assetFields: string[], prefix = '', selected?: string[]) => {
        const countries = selected || [...new Set(countryFields.flatMap(key => {
            const country = geographicCountry(source?.[key]);
            return country ? [country] : detectedCountries(text(source?.[key]));
        }))].sort();
        groups.push({ countryFields: countryFields.map(key => prefix + key), assetFields: assetFields.map(key => prefix + key),
            countries, assets: assetFields.map(key => ({ field: prefix + key, text: text(source?.[key]) })) });
    };
    code = code === 'S-10' ? 'EP-10' : code === 'S-11' ? 'EP-11' : code;
    if (code === 'EP-10' && intake?.values)
        intake = { ...intake.values, venues: intake.venues };
    if (isIntelligenceService(code))
        intake = cleanIntelligenceIntake(code, intake);
    else if (code === 'S-02')
        intake = cleanS02Intake(intake);
    else if (code === 'S-03')
        intake = cleanS03Intake(intake);
    else if (code === 'S-04')
        intake = cleanS04Intake(intake);
    else if (code === 'S-15')
        intake = cleanS15Intake(intake);
    else if (code === 'EP-10')
        intake = normalizeEpProjectIntake(intake || {});
    if (definitions[code])
        add(intake, ...definitions[code]);
    else if (code === 'EP-10') {
        const countries = [...new Set(text(intake?.operationalLocations).split('\n')
            .map(line => geographicCountry(line.split('|')[0])).filter(Boolean))].sort();
        add(intake, ['operationalLocations'], ['operationalLocations'], '', countries);
        for (let i = 0; i < (intake?.venues?.length || 0); i++)
            add(intake.venues[i], ['country'], ['name', 'address'], `venues.${i}.`);
    } else if (code === 'TRM-01') {
        if (Array.isArray(intake?.legs)) {
            for (let i = 0; i < intake.legs.length; i++)
                add(intake.legs[i], ['country'], ['destination', 'accommodation'], `legs.${i}.`);
            for (let i = 0; i < (intake?.countries?.length || 0); i++)
                add(intake.values?.s3?.[i], ['country'], ['operationalArea'], `values.s3.${i}.`, [geographicCountry(intake.countries[i])].filter(Boolean));
        } else
            for (let i = 0; i < (intake?.stops?.length || 0); i++)
                add(intake.stops[i], ['country'], ['city', 'accommodation'], `stops.${i}.`);
    }
    return { groups };
}

export function geographicConflicts(scope: GeographicScope): GeographicConflict[] {
    const conflicts: GeographicConflict[] = [];
    for (const group of scope.groups) {
        if (!group.countries.length)
            continue;
        const detected = new Set<string>();
        const fields = [...group.countryFields];
        for (const { field: key, text: value } of group.assets) {
            const extra = detectedCountries(value).filter(country => !group.countries.includes(country));
            if (extra.length) {
                fields.push(key);
                for (const country of extra)
                    detected.add(country);
            }
        }
        if (detected.size)
            conflicts.push({ countries: group.countries, detected: [...detected].sort(), fields });
    }
    return conflicts;
}

export const geographicScopeKey = (scope: GeographicScope) => JSON.stringify(scope.groups);
export const geographicReviewCurrent = (scope: GeographicScope, review?: GeographicReview | null) => Boolean(review?.confirmedByUid && review.confirmedAt && review.scopeKey === geographicScopeKey(scope));

export function serviceGeographicScope(service: any): GeographicScope {
    const code = String(service?.serviceCode || 'generic');
    if (['S-01', 'SRM-01', 'S-07', 'S-13', 'S-14', 'S-16'].includes(code))
        return { groups: [] };
    const field = code === 'S-10' || code === 'EP-10' ? 'epProjectIntake' : code === 'S-11' || code === 'EP-11' ? 'epSurveyIntake'
        : code === 'TRM-01' ? 'trmIntake' : ['S-05', 'S-06', 'S-07', 'S-12'].includes(code) ? 'intelligenceIntake'
            : `s${code.split('-')[1]}Intake`;
    const scopeCode = ['EP-11', 'S-11'].includes(code) && service?.epSurveyIntake?.country && !service.epSurveyIntake.venueCountry ? 'EP-11-legacy'
        : definitions[code] || ['EP-10', 'S-10', 'EP-11', 'S-11', 'TRM-01'].includes(code) ? code : 'generic';
    return geographicScope(scopeCode, service?.[field] || service);
}
